import { useState } from "react";
import { ChevronDown, ChevronUp, Mail, Phone, MessageSquare, Globe } from "lucide-react";
import { StructureContact } from "../lib/api";
import { ContactAvatar } from "./ContactAvatar";

interface ContactDisplayProps {
  contact: StructureContact;
}

export function ContactDisplay({ contact }: ContactDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const buildSignalLink = (value?: string | null) => {
    if (!value) return null;
    const trimmed = value.trim();
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    return `https://signal.me/#p/${encodeURIComponent(trimmed)}`;
  };

  const buildTelegramLink = (value?: string | null) => {
    if (!value) return null;
    const trimmed = value.trim().replace(/^@/, "");
    if (trimmed.length === 0) return null;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    return `https://t.me/${trimmed}`;
  };

  const getContactTypeColor = (type: string) => {
    switch (type) {
      case "primary":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700";
      case "backup":
        return "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-700";
      case "external":
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700";
      default:
        return "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700";
    }
  };

  const getContactTypeLabel = (type: string) => {
    switch (type) {
      case "primary": return "Primär";
      case "backup": return "Backup";
      case "external": return "Extern";
      default: return type;
    }
  };

  // Check which contact methods are available
  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  const hasSignal = !!contact.signal;
  const hasMatrix = !!contact.matrix;
  const hasTelegram = !!contact.telegram;
  const hasWebsite = !!contact.website;
  const hasAnyContact = hasEmail || hasPhone || hasSignal || hasMatrix || hasTelegram || hasWebsite;
  const signalLink = buildSignalLink(contact.signal);
  const telegramLink = buildTelegramLink(contact.telegram);

  const toggleExpanded = () => setIsExpanded((prev) => !prev);
  const onKeyToggle = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div className="border border-[var(--color-border)] rounded-lg bg-[var(--color-bg-secondary)] overflow-hidden">
      {/* Header - Always visible */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={onKeyToggle}
        className="w-full p-3 sm:p-4 text-left hover:bg-[var(--color-bg-tertiary)] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-border)]"
      >
        <div className="flex items-start gap-3">
          <ContactAvatar contact={contact} size={48} enablePreview className="flex-shrink-0" />

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <h4 className="font-semibold text-[var(--color-text)] text-sm sm:text-base truncate">
                {contact.name}
              </h4>
              {contact.role && (
                <span className="text-xs sm:text-sm text-[var(--color-muted)] truncate">
                  {contact.role}
                </span>
              )}
            </div>

            {/* Contact method icons - visible when collapsed */}
            {!isExpanded && hasAnyContact && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {hasEmail && (
                  <div className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
                    <Mail className="w-3 h-3" />
                  </div>
                )}
                {hasPhone && (
                  <div className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
                    <Phone className="w-3 h-3" />
                  </div>
                )}
                {(hasSignal || hasMatrix || hasTelegram) && (
                  <div className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
                    <MessageSquare className="w-3 h-3" />
                  </div>
                )}
                {hasWebsite && (
                  <div className="flex items-center gap-1 text-xs text-[var(--color-muted)]">
                    <Globe className="w-3 h-3" />
                  </div>
                )}
                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border ${getContactTypeColor(contact.contact_type)}`}>
                  {getContactTypeLabel(contact.contact_type)}
                </span>
                {contact.is_board_member && (
                  <span className="text-xs px-2 py-0.5 rounded-full border bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700">
                    Vorstand
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex-shrink-0">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-[var(--color-muted)]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[var(--color-muted)]" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-[var(--color-border)] p-3 sm:p-4 space-y-3 bg-[var(--color-bg)]">
          {/* Contact Type Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-1 rounded-full border ${getContactTypeColor(contact.contact_type)}`}>
              {getContactTypeLabel(contact.contact_type)}
            </span>
            {contact.is_board_member && (
              <span className="text-xs px-2 py-1 rounded-full border bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700">
                Vorstand
              </span>
            )}
          </div>

          {/* Contact Methods */}
          {hasAnyContact && (
            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wider">
                Kontaktmöglichkeiten
              </h5>

              {hasEmail && (
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                    <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">E-Mail</div>
                    <div className="text-sm text-[var(--color-text)] truncate">{contact.email}</div>
                  </div>
                </a>
              )}

              {hasPhone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                    <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">Telefon</div>
                    <div className="text-sm text-[var(--color-text)] truncate">{contact.phone}</div>
                  </div>
                </a>
              )}

              {hasSignal && (
                <a
                  href={signalLink || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors group min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                    <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">Signal</div>
                    <div className="text-sm text-[var(--color-text)] truncate font-mono w-full">
                      {signalLink ? contact.signal : contact.signal}
                    </div>
                  </div>
                </a>
              )}

              {hasMatrix && (
                <div className="flex items-center gap-3 p-2 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                    <MessageSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">Matrix</div>
                    <div className="text-sm text-[var(--color-text)] truncate font-mono">{contact.matrix}</div>
                  </div>
                </div>
              )}

              {hasTelegram && (
                <a
                  href={telegramLink || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center group-hover:bg-sky-200 dark:group-hover:bg-sky-900/50 transition-colors">
                    <MessageSquare className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">Telegram</div>
                    <div className="text-sm text-[var(--color-text)] truncate">
                      @{contact.telegram}
                    </div>
                  </div>
                </a>
              )}

              {hasWebsite && (
                <a
                  href={contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors group"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center group-hover:bg-amber-200 dark:group-hover:bg-amber-900/50 transition-colors">
                    <Globe className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[var(--color-muted)]">Website</div>
                    <div className="text-sm text-[var(--color-text)] truncate">{contact.website}</div>
                  </div>
                </a>
              )}
            </div>
          )}

          {/* Notes */}
          {contact.notes && (
            <div className="space-y-1">
              <h5 className="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wider">
                Notizen
              </h5>
              <p className="text-sm text-[var(--color-text)] whitespace-pre-wrap">
                {contact.notes}
              </p>
            </div>
          )}

          {/* Tags */}
          {contact.tags && contact.tags.length > 0 && (
            <div className="space-y-1">
              <h5 className="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wider">
                Tags
              </h5>
              <div className="flex flex-wrap gap-1">
                {contact.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-1 rounded-full bg-[var(--color-bg-secondary)] text-[var(--color-text)] border border-[var(--color-border)]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
